import { styled } from '@mui/material';
import type { ReactNode } from 'react';

import { AiIcon } from '../../../icons';

type Props = {
  /** `user` messages align to the end; `assistant` messages carry the AI tint. */
  author: 'user' | 'assistant';
  children: ReactNode;
  /** Extra content under the message (sources, actions). */
  footer?: ReactNode;
  'data-testid'?: string;
};

const ASSISTANT_TINT_OPACITY = 0.08;
const USER_TINT_OPACITY = 0.1;
const MAX_BUBBLE_WIDTH = '85%';
// The corner next to the author stays square, like a speech bubble tail.
const TAIL_RADIUS = 4;

const Row = styled('div', {
  shouldForwardProp: (prop) => prop !== 'author',
})<{ author: Props['author'] }>(({ theme, author }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: theme.spacing(1),
  justifyContent: author === 'user' ? 'flex-end' : 'flex-start',
}));

const Bubble = styled('div', {
  shouldForwardProp: (prop) => prop !== 'author',
})<{ author: Props['author'] }>(({ theme, author }) => ({
  ...theme.typography.body1,
  color: theme.palette.text.primary,
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  minWidth: 0,
  maxWidth: MAX_BUBBLE_WIDTH,
  padding: theme.spacing(1.5, 2),
  borderRadius: theme.shape.borderRadius,
  wordBreak: 'break-word',
  ...(author === 'user'
    ? {
        backgroundColor: theme.alpha(theme.palette.primary.main, USER_TINT_OPACITY),
        borderBottomRightRadius: TAIL_RADIUS,
      }
    : {
        backgroundColor: theme.alpha(theme.palette.ai.main, ASSISTANT_TINT_OPACITY),
        border: `1px solid ${theme.palette.divider}`,
        borderBottomLeftRadius: TAIL_RADIUS,
      }),
}));

const IconSlot = styled('div')(({ theme }) => ({
  display: 'flex',
  flexShrink: 0,
  paddingTop: theme.spacing(1.5),
}));

/**
 * One message of a conversation with the AI assistant.
 */
export function ChatMessage(props: Props) {
  return (
    <Row author={props.author} data-testid={props['data-testid'] ?? `chat-message-${props.author}`}>
      {props.author === 'assistant' ? (
        <IconSlot>
          <AiIcon fontSize="small" color="ai" />
        </IconSlot>
      ) : null}
      <Bubble author={props.author}>
        {props.children}
        {props.footer}
      </Bubble>
    </Row>
  );
}

export type { Props as ChatMessageProps };
